import React, { useState } from "react";
import { connect } from "react-redux";
import { Link, useRouteMatch } from "react-router-dom";
import ScheduleDayIcon from "../../../assets/icons/ScheduleDayIcon";

import {
    FileDoneOutlined,
    FileUnknownOutlined,
    MessageOutlined,
    ReadOutlined,
    DatabaseOutlined,
    PieChartOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    ReloadOutlined,
    SwapOutlined,
    TeamOutlined,
    UnorderedListOutlined,
    SearchOutlined,
    MedicineBoxOutlined,
    UserSwitchOutlined,
    HistoryOutlined,
    ArrowRightOutlined
} from "@ant-design/icons";

import { PhysicianTagEnum } from "../../../types/enums"
import DashBoardIcon from "../../../assets/icons/DashBoardIcon";
import CaseIcon from "../../../assets/icons/CaseIcon";
import ConsultedCaseIcon from "../../../assets/icons/ConsultedCaseIcon";

import {
    StyledMenu,
    StyledBadge,
    MenuItemContent,
} from './MainMenu.Styles'

const MainMenu = (props) => {
    let { url } = useRouteMatch();
    const [openKeys, setOpenKeys] = useState([]);

    const {
        rxChangeCaseCount,
        rxRenewalCaseCount,
        incompleteCasesCount,
        MessageCount,
        additionalInfoCaseUpdateCount,
        referralCasesCount,
        caseTicketNotificationCount,
        pendingPrescriptionCaseNotification,
        physicianDetails,
        caseQueue,
        oneClickPrescribeQueueCount
    } = props;

    const physicianTags = physicianDetails?.tags?.map(tag => tag.toLowerCase())
    const physicianNeedsCasesReviewed = physicianTags?.includes(PhysicianTagEnum.NEEDS_REVIEW)
    const isReviewingPhysician = physicianTags?.includes(PhysicianTagEnum.REVIEWER)
    const isLeadPhysician = physicianTags?.map(tag => tag.toLowerCase()).includes(PhysicianTagEnum.LEAD_PHYSICIAN)

    const handleOpenChange = (keys) => {
        setOpenKeys(keys);
    };

    const items = [
        {
            key: "/dashboard",
            label: (
                <Link to={`${url}`}>
                    <MenuItemContent>
                        <DashBoardIcon />
                        Dashboard
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/cases",
            label: (
                <Link to={`${url}/physician/cases`}>
                    <MenuItemContent>
                        <SearchOutlined />
                        Cases
                    </MenuItemContent>
                </Link>
            ),
        },
        ...(isLeadPhysician ? [{
            key: "/dashboard/physician/one-click-prescribe",
            label: (
                <Link to={`${url}/physician/one-click-prescribe`}>
                    <MenuItemContent>
                        <MedicineBoxOutlined />
                        CEV
                        <StyledBadge count={oneClickPrescribeQueueCount} />
                    </MenuItemContent>
                </Link>
            ),
        }] : []),
        {
            key: "/dashboard/physician/case-queue",
            label: (
                <Link to={`${url}/physician/case-queue`}>
                    <MenuItemContent>
                        <UnorderedListOutlined />
                        Case Queue
                        <StyledBadge count={caseQueue?.length} />
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/caselist",
            label: (
                <Link to={`${url}/physician/caselist`}>
                    <MenuItemContent>
                        <CaseIcon />
                        Pending Prescription
                        <StyledBadge count={pendingPrescriptionCaseNotification} />
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/sync-cases",
            label: (
                <Link to={`${url}/physician/sync-cases`}>
                    <MenuItemContent>
                        <CaseIcon />
                        Sync Cases
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/additional-info-required",
            label: (
                <Link to={`${url}/physician/additional-info-required`}>
                    <MenuItemContent>
                        <FileUnknownOutlined />
                        Additional Info Required
                        <StyledBadge count={additionalInfoCaseUpdateCount} />
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/updateprescribe",
            label: (
                <Link to={`${url}/physician/updateprescribe`}>
                    <MenuItemContent>
                        <SwapOutlined />
                        Rx Change Requests
                        <StyledBadge count={rxChangeCaseCount} />
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/rx-requests",
            label: (
                <Link to={`${url}/physician/rx-change-requests`}>
                    <MenuItemContent>
                        <ReloadOutlined />
                        Rx Renewal Requests
                        <StyledBadge count={rxRenewalCaseCount} />
                    </MenuItemContent>
                </Link>
            ),
        },
        ...((isReviewingPhysician || physicianNeedsCasesReviewed) ? [{
            key: "/dashboard/physician/referrals",
            label: (
                <Link to={`${url}/physician/referrals`}>
                    <MenuItemContent>
                        <EyeOutlined />
                        {(physicianNeedsCasesReviewed ? 'In Review' : 'Needs Review')}
                        <StyledBadge count={referralCasesCount} />
                    </MenuItemContent>
                </Link>
            ),
        }] : []),
        {
            key: "/dashboard/physician/reassigned-cases",
            label: (
                <Link to={`${url}/physician/reassigned-cases`}>
                    <MenuItemContent>
                        <UserSwitchOutlined />
                        Reassigned Cases
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/incomplete-cases",
            label: (
                <Link to={`${url}/physician/incomplete-cases`}>
                    <MenuItemContent>
                        <ExclamationCircleOutlined />
                        Incomplete Cases
                        <StyledBadge count={incompleteCasesCount} />
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/messages",
            label: (
                <Link to={`${url}/physician/messages`}>
                    <MenuItemContent>
                        <MessageOutlined />
                        Messages
                        <StyledBadge count={MessageCount} />
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "/dashboard/physician/case-tickets",
            label: (
                <Link to={`${url}/physician/case-tickets`}>
                    <MenuItemContent>
                        <DatabaseOutlined />
                        Case Tickets
                        <StyledBadge count={caseTicketNotificationCount} />
                    </MenuItemContent>
                </Link>
            ),
        },
        {
            key: "submenu",
            popupClassName: "main-menu-popup",
            label: (
                <MenuItemContent>
                    <ArrowRightOutlined />
                    More Options
                </MenuItemContent>
            ),
            children: [
                {
                    key: "/dashboard/physician/consultedcase",
                    label: (
                        <Link to={`${url}/physician/consultedcase`}>
                            <MenuItemContent>
                                <FileDoneOutlined />
                                Consulted Cases
                            </MenuItemContent>
                        </Link>
                    ),
                },
                {
                    key: "/dashboard/physician/consultedcase-noRX",
                    label: (
                        <Link to={`${url}/physician/consultedcase-noRX`}>
                            <MenuItemContent>
                                <ConsultedCaseIcon />
                                Consulted Cases (No RX)
                            </MenuItemContent>
                        </Link>
                    ),
                },
                ...(isLeadPhysician ? [{
                    key: "/dashboard/physician/other-physicians-cases",
                    label: (
                        <Link to={`${url}/physician/other-physicians-cases`}>
                            <MenuItemContent>
                                <TeamOutlined />
                                Other Providers' Cases
                            </MenuItemContent>
                        </Link>
                    ),
                }] : []),
                {
                    key: "/dashboard/physician/history",
                    label: (
                        <Link to={`${url}/physician/history`}>
                            <MenuItemContent>
                                <HistoryOutlined />
                                History
                            </MenuItemContent>
                        </Link>
                    ),
                },
                {
                    key: "/dashboard/physician/availability",
                    label: (
                        <Link to={`${url}/physician/availability`}>
                            <MenuItemContent>
                                <ScheduleDayIcon />
                                My Availability
                            </MenuItemContent>
                        </Link>
                    ),
                },
                {
                    key: "/dashboard/physician/templates",
                    label: (
                        <Link to={`${url}/physician/templates`}>
                            <MenuItemContent>
                                <ReadOutlined />
                                Templates
                            </MenuItemContent>
                        </Link>
                    ),
                },
                {
                    key: "/dashboard/physician/articles",
                    label: (
                        <Link to={`${url}/physician/articles`}>
                            <MenuItemContent>
                                <ReadOutlined />
                                Articles
                            </MenuItemContent>
                        </Link>
                    ),
                },
                {
                    key: "/dashboard/physician/reports",
                    label: (
                        <Link to={`${url}/physician/reports`}>
                            <MenuItemContent>
                                <PieChartOutlined />
                                Reports
                            </MenuItemContent>
                        </Link>
                    ),
                },
            ],
        },
    ];

    return (
        <StyledMenu
            mode="vertical"
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={items}
        />
    );
};

function mapStateToProps(state) {
    const { MessageCount } = state.message;
    const { additionalInfoCaseUpdateCount } = state.additionalInfoRequired;
    const { referralCasesCount } = state.referrals
    const { rxChangeCaseCount } = state.rxChange;
    const { rxRenewalCaseCount } = state.rxRenewal;
    const { incompleteCasesCount } = state.incompleteCases;
    const { caseTicketNotificationCount } = state.caseTickets;
    const { pendingPrescriptionCaseNotification } = state.notifications;
    const { physicianDetails } = state.physicianDetails
    const { caseQueue } = state.caseQueue
    const { oneClickPrescribeQueueCount } = state.oneClickPrescribeQueue

    return {
        MessageCount,
        additionalInfoCaseUpdateCount,
        referralCasesCount,
        rxChangeCaseCount,
        rxRenewalCaseCount,
        incompleteCasesCount,
        caseTicketNotificationCount,
        pendingPrescriptionCaseNotification,
        physicianDetails,
        caseQueue,
        oneClickPrescribeQueueCount
    };
}

export default connect(mapStateToProps)(MainMenu);
